import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { catchError, from, Observable, of, timer } from 'rxjs'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { first, map, switchMap, take } from 'rxjs/operators'
import { RruleHelper } from '../../recurrence/rruleHelper'
import { ConfigService } from '../../../services/config.service'
import { DateUtils } from '../../../services/date-utils.service'
import { EditorAssistance, EditorAssistanceAssignment } from '../../../models/EditorAssistance'
import { AssistanceEditorForm, mapFormFieldsToAssistance } from './AssistanceEditorForm'
import { set } from 'date-fns'
import { RecurrenceUtil } from '../../recurrence/RecurrenceUtil'
import type { AssistanceEditorComponent } from './assistance-editor.component'
import { DateTime } from '../../../models/DateTime/DateTime'

const TAG = 'AssistanceEditorValidators'

/**
 * Returns a validation error if the DateTime control has incomplete value (i.e. is missing date or time)
 */
export const requiredDateTimeValidator = (control: AbstractControl): ValidationErrors | null => {
  if (control.value && (control.value as DateTime).isComplete) {
    return null
  }
  return { required: true }
}

/**
 * Validates if the planned DateTime To is later than the planned DateTime From.
 *
 */
export const plannedDatetimeToLaterThanFromValidator: ValidatorFn = (fg: AssistanceEditorForm) => {
  const plannedFrom = fg.value.plannedFrom?.toDate(null)
  const plannedTo = fg.value.plannedTo?.toDate(null)

  if (!plannedFrom || !plannedTo) return null

  return plannedFrom.getTime() < plannedTo.getTime() ? null : { validPlannedDatetimeToLaterThanFrom: true }
}

/**
 * Checks if the planned date and time is sooner than allowed in app config.
 *
 * @param assistance - Assistance in state before validation / save.
 * @param configService - Config service for getting config values from DB
 * @returns An async validator function.
 */
export function isPlannedDateTimeSoonerThanConfigAllowsAsyncValidator(
  assistance: EditorAssistance,
  configService: ConfigService,
): AsyncValidatorFn {
  return (fg: AssistanceEditorForm): Observable<ValidationErrors | null> => {
    const plannedFrom = fg.value.plannedFrom?.toDate(null)
    const plannedTo = fg.value.plannedTo?.toDate(null)

    if (!plannedFrom || !plannedTo) return of(null)
    if (!havePlannedTimesChanged(fg, assistance)) return of(null)

    return from(configService.getEditingPastAssistanceByCoordinatorDays()).pipe(
      first(),
      map((days) => {
        if (
          plannedFrom.getTime() > DateUtils.subtractDaysFromDate(new Date(), days).getTime() &&
          plannedTo.getTime() > DateUtils.subtractDaysFromDate(new Date(), days).getTime()
        ) {
          return null
        } else {
          return { isPlannedDateTimeSoonerThanConfigAllows: days }
        }
      }),
      first(),
    )
  }
}

export const realizedDatetimeToLaterThanFromValidator: ValidatorFn = (fg: AssistanceEditorForm) => {
  const realizedFrom = fg.value.realizedFrom?.toDate(null)
  const realizedTo = fg.value.realizedTo?.toDate(null)

  if (!realizedFrom || !realizedTo) return null

  return realizedFrom.getTime() < realizedTo.getTime() ? null : { validRealizedDatetimeToLaterThanFrom: true }
}

export const realizedToOrFromOnlyValidator: ValidatorFn = (fg: AssistanceEditorForm) => {
  const { realizedFrom, realizedTo } = fg.value
  if (!realizedFrom && !realizedTo) {
    return null
  }
  const isPartial = (value: any) => (value?.date && !value?.time) || (value?.time && !value?.date)
  if (isPartial(realizedTo) && isPartial(realizedFrom)) {
    return { realizedFromToPartial: true }
  }
  if (isPartial(realizedTo)) {
    return { realizedToPartial: true }
  }
  if (isPartial(realizedFrom)) {
    return { realizedFromPartial: true }
  }
  return null
}

/**
 * Requirements:
 * Recurrence UNTIL has to be later than plannedFrom
 */
export const recurrenceUntilLaterThanPlannedFromValidator: ValidatorFn = (fg: AssistanceEditorForm) => {
  const plannedFrom = fg.value.plannedFrom?.toDate(null)
  if (!plannedFrom) return null

  const rrule = fg.value.rrule
  if (!rrule) {
    return null
  }

  const recurrenceUntilDate = RruleHelper.extractUntilDateFromStringRrule(rrule)
  if (!recurrenceUntilDate) {
    return null
  }

  const minRecurrenceEnd = new Date(plannedFrom)
  set(minRecurrenceEnd, {
    date: minRecurrenceEnd.getDate() + 1,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  })

  return recurrenceUntilDate.getTime() >= minRecurrenceEnd.getTime()
    ? null
    : { recurrenceUntilLaterThanPlannedFrom: true }
}

export function recurrentAssistanceHasAtLeastOneOccurrence(oldAssistance: EditorAssistance): ValidatorFn {
  return (fg: AssistanceEditorForm) => {
    const rrule = fg.value.rrule
    if (!rrule) {
      return null
    }

    const occurrences = expandAssistanceInForm(fg, oldAssistance)

    console.log(TAG, 'recurrentAssistanceHasAtLeastOneOccurrence', occurrences)

    return !occurrences.length ? { recurrentAssistanceHasAtLeastOneOccurrence: true } : null
  }
}

export const validateRequiredFieldsWhenFormDisabled: ValidatorFn = (fg: AssistanceEditorForm) => {
  for (const field in fg.controls) {
    // 'field' is a string
    const control = fg.get(field) // 'control' is a FormControl
    if (control instanceof FormControl && control.hasValidator(Validators.required) && !control.value) {
      return { validateRequiredFieldsWhenFormDisabled: true }
    }
  }
  return null
}

export const recurrenceUntilLaterThan1970: ValidatorFn = (fg: AssistanceEditorForm) => {
  const rrule = fg.value.rrule
  if (!rrule) {
    return null
  }

  const recurrenceUntilDate = RruleHelper.extractUntilDateFromStringRrule(rrule)
  if (!recurrenceUntilDate) {
    return null
  }

  return recurrenceUntilDate.getTime() > new Date(0).getTime() ? null : { recurrenceUntilLaterThanPlannedFrom: true }
}

/**
 * Checks whether assistance is Late Ordered and Recurrent. If the assistance is an exception, it should be OK to update if late ordered.
 *
 */
export function isLateOrderedAndRecurrentAsyncValidator(): AsyncValidatorFn {
  return (fg: AssistanceEditorForm): Observable<ValidationErrors | null> => {
    const isRecurrent = !!fg.value.rrule
    const isLateOrder = fg.controls.isLateOrder.value

    if (!isRecurrent) return of(null)

    if (isLateOrder && isRecurrent) {
      return of({ isLateOrderedAndRecurrent: true })
    }
    return of(null)
  }
}

/**
 * Checks if the planned times in the form have changed compared to the old assistance.
 *
 * @param fg The form group used for editing assistance.
 * @param oldAssistance The old assistance object to compare with.
 * @return Returns true if the planned times have changed, otherwise false.
 */
function havePlannedTimesChanged(fg: AssistanceEditorForm, oldAssistance: EditorAssistance): boolean {
  const plannedFrom = fg.value.plannedFrom?.toDate(null)?.getTime()
  const plannedTo = fg.value.plannedTo?.toDate(null)?.getTime()
  const oldPlannedFrom = oldAssistance.getDateFrom()?.getTime()
  const oldPlannedTo = oldAssistance.getDateTo()?.getTime()

  return plannedFrom !== oldPlannedFrom || plannedTo !== oldPlannedTo
}

/**
 * Longest transit among the assistance's assignments, or undefined when there are none (creation flow) —
 * undefined lets the backend fall back to the tenant default transit duration.
 */
export function getLongestTransitDurationMinutes(
  assignments: EditorAssistanceAssignment[] | undefined,
): number | undefined {
  const assignmentTransits = (assignments ?? []).map((a) => a.transitDurationMinutes ?? 0)
  const hasAssignments = assignmentTransits.length > 0
  return hasAssignments ? Math.max(...assignmentTransits) : undefined
}

export function isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator(
  assistanceService: AssistanceService,
  editorComponent: AssistanceEditorComponent,
): AsyncValidatorFn {
  console.log(TAG, 'isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator')
  return (fg: AssistanceEditorForm): Observable<ValidationErrors | null> => {
    console.log(
      TAG,
      'isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator validator fn',
      editorComponent.assistance,
    )
    const assistanceBeforeValueChange = editorComponent.assistance
    const plannedFrom = fg.value.plannedFrom?.toDate(null)
    const plannedTo = fg.value.plannedTo?.toDate(null)

    const currentAssistanceId = RecurrenceUtil.parseURID(fg.value.urid)?.id || null

    if (!plannedFrom || !plannedTo) return of(null)

    // console.log(TAG, 'isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator havePlannedTimesChanged', havePlannedTimesChanged(fg, oldAssistance))
    // console.log(TAG, 'isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator rrule', fg.value.rrule, oldAssistance.rrule)

    if (
      !havePlannedTimesChanged(fg, assistanceBeforeValueChange) &&
      fg.value.rrule === assistanceBeforeValueChange.rrule
    )
      return of(null)

    return timer(500).pipe(
      // map(() => firstValueFrom(assistanceService.isOverlappingAnyAssistanceForTheSameClient(plannedFrom, plannedTo, fg.value.clientId, currentAssistanceId, oldAssistance, fg.value.rrule))),
      // switchMap(() => firstValueFrom(of([{from: new Date(), to: new Date()}]))),
      // log('overlap validator runs', fg.value.plannedFrom, fg.value.plannedTo, fg.value.clientId, currentAssistanceId, oldAssistance, fg.value.rrule),
      switchMap(() =>
        assistanceService
          .isOverlappingAnyAssistanceForTheSameClient(
            fg.value.plannedFrom?.toDate(),
            fg.value.plannedTo?.toDate(),
            fg.value.clientId,
            currentAssistanceId,
            assistanceBeforeValueChange,
            fg.value.rrule,
            getLongestTransitDurationMinutes(fg.value.assistantAssignments),
          )
          .pipe(take(1)),
      ),
      // tap(e => console.log('overlap', e)),
      map((overlappedAssistances) => {
        if (overlappedAssistances?.length > 0) {
          return {
            isOverlappingAnyOtherAssistanceForTheSameClient: overlappedAssistances,
          }
        } else {
          return null
        }
      }),
      // tap(e => console.log('overlap validator returning', e)),
      take(1),
      catchError((err) => {
        console.error(err)
        return null
      }),
    )
  }
}

export function isOverlappingAnyOtherAssistanceForTheSameAssistantAsyncValidator(
  assistanceService: AssistanceService,
  assistanceBeforeValueChange: EditorAssistance,
): AsyncValidatorFn {
  console.log(TAG, 'isOverlappingAnyOtherAssistanceForTheSameAssistantAsyncValidator')
  return (fg: AssistanceEditorForm): Observable<ValidationErrors | null> => {
    console.log(TAG, 'isOverlappingAnyOtherAssistanceForTheSameAssistantAsyncValidator validator fn')
    const plannedFrom = fg.value.plannedFrom?.toDate(null)
    const plannedTo = fg.value.plannedTo?.toDate(null)
    const assistantIds = fg.value.assistantAssignments ? fg.value.assistantAssignments.map((a) => a.assistantId) : []

    const currentAssistanceId = RecurrenceUtil.parseURID(fg.value.urid)?.id || null

    if (!plannedFrom || !plannedTo || !currentAssistanceId) {
      return of(null)
    }
    if (
      !havePlannedTimesChanged(fg, assistanceBeforeValueChange) &&
      fg.value.rrule === assistanceBeforeValueChange.rrule
    ) {
      return of(null)
    }

    return timer(500).pipe(
      switchMap(() =>
        assistanceService
          .isOverlappingAnyAssistanceForTheSameAssistant(
            fg.value.plannedFrom?.toDate(),
            fg.value.plannedTo?.toDate(),
            assistantIds,
            currentAssistanceId,
          )
          .pipe(take(1)),
      ),
      map((overlappedAssistances) => {
        if (overlappedAssistances?.length > 0) {
          return {
            isOverlappingAnyOtherAssistanceForTheSameAssistant: overlappedAssistances,
          }
        } else {
          return null
        }
      }),
      take(1),
      catchError((err) => {
        console.error(err)
        return null
      }),
    )
  }
}

function expandAssistanceInForm(form: AssistanceEditorForm, inputAssistance: EditorAssistance) {
  const formValues = form.getRawValue() // for potentially disabled fields

  if (!formValues.rrule) {
    return null
  }
  if (!formValues.urid) {
    console.error('URID is missing', formValues)
  }
  const assistanceFromForm = EditorAssistance.createFromProps(
    EditorAssistance,
    mapFormFieldsToAssistance(formValues, formValues.urid),
  )
  console.log('expandAssistanceInForm', assistanceFromForm)
  return assistanceFromForm.expand(null, false)
}
